import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Zmaterialdetails } from './zmaterialdetails';
import { Zmedia } from './zmedia';
import { ZVideo } from './ZVideo';
import { ZVideoSafeURL } from './ZVideoSafeURL';
import { ZMediaText } from './z-media-text.class';
import { ZHtmlContent } from './ZHtmlContent';
import { MachineData } from './machinedata';
import { Observable, BehaviorSubject, tap, of, map } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { ParametersService } from '../parameters/parameters.service';
import { MechatronicPlanType, MediaTypes } from '../../types/media.type';
import { ZMediaTextDto } from '@app/dtos/z-media-text.dto';
import { Locale } from '@app/models/locales';

@Injectable({
    providedIn: 'root',
})
export class MachineInfosService {
    private fetchedMachine: string;
    private fetchedLocale: Locale;
    private locale: Locale;
    private fahrzeugNr: string;
    private _viewMode: string;

    private readonly _isFetchRunning$ = new BehaviorSubject<boolean>(false);
    private readonly _machineData$ = new BehaviorSubject<MachineData>(null);

    private readonly fetchingDetails: Map<string, BehaviorSubject<boolean>> =
        new Map([
            ['highlights', new BehaviorSubject<boolean>(false)],
            ['documents', new BehaviorSubject<boolean>(false)],
            ['specsheet', new BehaviorSubject<boolean>(false)],
            ['fitmentsOptional', new BehaviorSubject<boolean>(false)],
            ['fitmentsFitted', new BehaviorSubject<boolean>(false)],
            ['media', new BehaviorSubject<boolean>(false)]
        ]);

    private readonly _highlights$ = new BehaviorSubject<string>('');
    private readonly _specsheet$ = new BehaviorSubject<string>('');
    private readonly _fitmentsOptional$ = new BehaviorSubject<Zmaterialdetails[]>([]);
    private readonly _fitmentsFitted$ = new BehaviorSubject<Zmaterialdetails[]>([]);
    private readonly _videos$ = new BehaviorSubject<ZVideoSafeURL[]>([]);
    private readonly _images$ = new BehaviorSubject<string[]>([]);
    private readonly _operationManuals$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _retrofitManuals$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _brochures$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _sparepartLists$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _lubricationPlans$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _mechatronicPlans$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _assemblyInstructions$ = new BehaviorSubject<ZMediaText[]>([]);
    private readonly _repairingInstructions$ = new BehaviorSubject<ZMediaText[]>([]);
    private userLang: string;

    constructor(
        private readonly http: HttpClient,
        private readonly parameterService: ParametersService,
        private readonly sanitizer: DomSanitizer
    ) {
        this.parameterService.getParametersObservable().subscribe(
            params => {
                if (!params.country) {
                    return;
                }
                this.locale = params.country;

                if (!params.fahrzeugNr) {
                    return;
                }
                this.fahrzeugNr = params.fahrzeugNr;

                this.userLang = params.user?.languageCode ?? params.country.code?.split('_')?.[0];
                this.checkForRefetch();
            });
    }

    get images$(): Observable<string[]> {
        return this._images$.asObservable();
    }

    get isFetchRunning$(): Observable<boolean> {
        return this._isFetchRunning$.asObservable();
    }

    get viewMode(): string {
        return this._viewMode;
    }

    set viewMode(viewMode: string) {
        this._viewMode = viewMode;
    }

    get fitmentsOptional$(): Observable<Zmaterialdetails[]> {
        return this._fitmentsOptional$.asObservable();
    }

    get fitmentsOptionalFetchRunning$(): Observable<boolean> {
        return this.fetchingDetails.get('fitmentsOptional').asObservable();
    }

    get fitmentsFitted$(): Observable<Zmaterialdetails[]> {
        return this._fitmentsFitted$.asObservable();
    }

    get fitmentsFittedFetching$(): Observable<boolean> {
        return this.fetchingDetails.get('fitmentsFitted').asObservable();
    }

    get videos$(): Observable<ZVideoSafeURL[]> {
        return this._videos$.asObservable();
    }

    get mediaFetching$(): Observable<boolean> {
        return this.fetchingDetails.get('media');
    }

    get lubricationPlans$(): Observable<ZMediaText[]> {
        return this._lubricationPlans$.asObservable();
    }

    get mechatronicPlans$(): Observable<ZMediaText[]> {
        return this._mechatronicPlans$.asObservable();
    }

    get sparepartLists$(): Observable<ZMediaText[]> {
        return this._sparepartLists$.asObservable();
    }

    get documentsFetching$(): Observable<boolean> {
        return this.fetchingDetails.get('documents');
    }

    get brochures$(): Observable<ZMediaText[]> {
        return this._brochures$.asObservable();
    }

    get operationManuals$(): Observable<ZMediaText[]> {
        return this._operationManuals$.asObservable();
    }

    get retrofitManuals$(): Observable<ZMediaText[]> {
        return this._retrofitManuals$.asObservable();
    }

    get assemblingInstructions$(): Observable<ZMediaText[]> {
        return this._assemblyInstructions$.asObservable();
    }

    get repairingInstructions$(): Observable<ZMediaText[]> {
        return this._repairingInstructions$.asObservable();
    }

    get specsheet$(): Observable<string> {
        return this._specsheet$.asObservable();
    }

    get specsheetFetching$(): Observable<boolean> {
        return this.fetchingDetails.get('specsheet');
    }

    get highlights$(): Observable<string> {
        return this._highlights$.asObservable();
    }

    get machineData$(): Observable<MachineData> {
        return this._machineData$.asObservable();
    }

    fetchMachineData(fahrzeugNr: string, locale: string, global = false): Observable<MachineData> {
        if (!fahrzeugNr) {
            return null;
        }
        return this.http.get<MachineData>(`${environment.machinedataUrl}(IManr='${fahrzeugNr}',ILocaleLayer='${locale}')?$format=json`).pipe(
            tap(data => {
                if (global) {
                    const machineData = data;
                    if (data.chassisnr !== '') {
                        machineData.matnr = data.chassisnr;
                    } else if (data.matnr !== '') {
                        machineData.matnr = data.matnr;
                    } else {
                        machineData.matnr = fahrzeugNr;
                    }

                    this._machineData$.next(machineData);
                }
            })
        );
    }

    private fetchMedia(fahrzeugNr: string, locale: string): Observable<Zmedia[]> {
        if (!fahrzeugNr) {
            return of([]);
        }

        const videoSafeUrls = [];
        return this.http.get<Zmedia[]>(`${environment.mediaUrl}&$filter=ILocaleLayer eq '${locale}' and IManr eq '${fahrzeugNr}'`).pipe(
            tap(data => {
                const videos = data.filter(video => video.type === MediaTypes.Video) as ZVideo[];
                videos.forEach(vid => {
                    const zvid: ZVideoSafeURL = {
                        url: this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${vid.url}?version=3`),
                        urlThumb: this.sanitizer.bypassSecurityTrustResourceUrl(`https://i1.ytimg.com/vi/${vid.url}/mqdefault.jpg`),
                        bezei: vid.bezei
                    };
                    videoSafeUrls.push(zvid);
                });


                this._videos$.next(videoSafeUrls);

                const images = data.filter(image => image.type === MediaTypes.Image)
                    .map(image => image.url.toLocaleLowerCase().replace('http:', '').replace('https:', ''));
                this._images$.next(images);
            })
        );
    }

    private checkForRefetch() {
        if ((this.fetchedMachine !== this.fahrzeugNr
            || this.fetchedLocale !== this.locale)
            && this.locale && this.fahrzeugNr) {
            this.fetch();
        }
    }

    private fetch() {
        this._isFetchRunning$.next(true);

        // remove old results
        this._highlights$.next(null);
        this._specsheet$.next(null);
        this._fitmentsOptional$.next(null);
        this._fitmentsFitted$.next(null);
        this._videos$.next(null);
        this._images$.next(null);
        this._operationManuals$.next(null);
        this._brochures$.next(null);
        this._retrofitManuals$.next(null);
        this._sparepartLists$.next(null);
        this._lubricationPlans$.next(null);
        this._machineData$.next(null);

        this.fetchMachineData(this.fahrzeugNr, this.locale.code, true).subscribe({
            next: () => {
                this.reduceServices();
                this.fetchDependentEntities();
            },
            error: () => {
                this.reduceServices();
            }
        });
    }

    private fetchDependentEntities() {
        this.fetchingDetails.get('highlights').next(true);
        this.fetchHighlights(this.fahrzeugNr, this.locale.code).subscribe({
            next: () => {
                this.fetchingDetails.get('highlights').next(false);
                this.reduceServices();
            },
            error: () => {
                this.fetchingDetails.get('highlights').next(false);
                this.reduceServices();
            }
        });


        this.fetchingDetails.get('specsheet').next(true);
        this.fetchSpecsheet(this.fahrzeugNr, this.locale.code).subscribe({
            next: () => {
                this.fetchingDetails.get('specsheet').next(false);
                this.reduceServices();
            },
            error: () => {
                this.fetchingDetails.get('specsheet').next(false);
                this.reduceServices();
            }
        });

        this.fetchingDetails.get('fitmentsFitted').next(true);
        this.fetchFitmentsFitted(this.fahrzeugNr, this.locale.code).subscribe({
            next: () => {
                this.fetchingDetails.get('fitmentsFitted').next(false);
                this.reduceServices();
            }, error: () => {
                this.fetchingDetails.get('fitmentsFitted').next(false);
                this.reduceServices();
            }
        });

        this.fetchingDetails.get('fitmentsOptional').next(true);
        this.fetchFitmentsOptional(this.fahrzeugNr, this.locale.code).subscribe({
            next: () => {
                this.fetchingDetails.get('fitmentsOptional').next(false);
                this.reduceServices();
            },
            error: () => {
                this.fetchingDetails.get('fitmentsOptional').next(false);
                this.reduceServices();
            }
        });

        this.fetchingDetails.get('media').next(true);
        this.fetchMedia(this.fahrzeugNr, this.locale.code).subscribe({
            next: () => {
                this.fetchingDetails.get('media').next(false);
                this.reduceServices();
            }, error: () => {
                this.fetchingDetails.get('media').next(false);
                this.reduceServices();
            }
        });

        this.fetchingDetails.get('documents').next(true);

        this.fetchDocuments(this.fahrzeugNr).subscribe({
            next: () => {
                this.fetchingDetails.get('documents').next(false);
                this.reduceServices();
            }, error: () => {
                this.fetchingDetails.get('documents').next(false);
                this.reduceServices();
            }
        });
    }

    private reduceServices() {
        let running = 0;

        this.fetchingDetails.forEach((o: BehaviorSubject<boolean>) => {
            if (o.value) {
                running++;
            }
        });

        if (running === 0) { // fetching complete, prevent loading again
            this.fetchedMachine = this.fahrzeugNr;
            this.fetchedLocale = this.locale;

            this._isFetchRunning$.next(false);
        } else {
            this._isFetchRunning$.next(true);
        }
    }

    private fetchFitmentsFitted(fahrzeugNr: string, locale: string): Observable<Zmaterialdetails[]> {
        if (!fahrzeugNr) {
            return of([]);
        }

        return this.http.get<Zmaterialdetails[]>(`${environment.fitmentsFittedUrl}&$filter=ILocaleLayer eq '${locale}' and IManr eq '${fahrzeugNr}'`)
            .pipe(
                tap(data => this._fitmentsFitted$.next(data))
            );
    }

    private fetchFitmentsOptional(fahrzeugNr: string, locale: string): Observable<Zmaterialdetails[]> { // : Promise<Zmaterialdetails[]>
        if (!fahrzeugNr) {
            return of([]);
        }

        return this.http.get<Zmaterialdetails[]>(`${environment.fitmentsOptionalUrl}&$filter=ILocaleLayer eq '${locale}' and IManr eq '${fahrzeugNr}'`)
            .pipe(
                tap(data => this._fitmentsOptional$.next(data))
            );
    }

    private fetchDocuments(fahrzeugNr: string): Observable<ZMediaText[]> {
        if (!fahrzeugNr) {
            return of([]);
        }
        const specialViewModes = ['poetdoc']
        const viewModeParameter = specialViewModes.includes(this._viewMode) ? `and IView eq '${this._viewMode}'` : '';

        return this.http.get<ZMediaTextDto[]>(
            `${environment.documentsUrl}&$filter=IManr eq '${fahrzeugNr}' ${viewModeParameter}`
        ).pipe(
            map(medias => medias.map(media => new ZMediaText(media))),
            tap(data => {
                this.extractBrochures(data);
                this.extractOperationManuals(data);
                this.extractRetrofitManuals(data);
                this.extractAssemblingInstructions(data);
                this.extractRepairingInstructions(data);
                this.extractSparepartLists(data);
                this.extractLubricationPlans(data);
                this.extractMechatronicPlans(data);
            }));
    }

    private extractSparepartLists(jsonData: ZMediaText[]) {
        const reducedDocuments = this.extractDocuments(jsonData, MediaTypes.SparepartsList);

        if (reducedDocuments.length > 0) {
            this._sparepartLists$.next(reducedDocuments);
        }
    }

    private extractLubricationPlans(jsonData: ZMediaText[]) {
        const reducedDocuments = this.extractDocuments(jsonData, MediaTypes.LubricationPlan);

        if (reducedDocuments.length > 0) {
            this._lubricationPlans$.next(reducedDocuments);
        }
    }

    private extractMechatronicPlans(jsonData: ZMediaText[]) {
        const plans = jsonData.filter(doc => MediaTypes.MechatronicPlanTypes.includes(doc.type as MechatronicPlanType));
        const reducedPlans = this.reduceDocuments(plans, (a, b) => a.equalsIgnoringLanguage(b));

        if (reducedPlans.length > 0) {
            this._mechatronicPlans$.next(reducedPlans);
        }
    }

    private extractBrochures(jsonData: ZMediaText[]) {
        const reducedDcouments = this.extractDocuments(jsonData, MediaTypes.Brochure);

        if (reducedDcouments.length > 0) {
            this._brochures$.next(reducedDcouments);
        }
    }

    private extractOperationManuals(jsonData: ZMediaText[]) {
        const reducedDocuments = this.extractDocuments(jsonData, MediaTypes.OperationManual);

        if (reducedDocuments.length > 0) {
            this._operationManuals$.next(reducedDocuments);
        }
    }

    private extractRetrofitManuals(jsonData: ZMediaText[]) {
        const reducedDocuments = this.extractDocuments(jsonData, MediaTypes.RetrofitManual);

        if (reducedDocuments.length > 0) {
            this._retrofitManuals$.next(reducedDocuments);
        }
    }

    private extractAssemblingInstructions(jsonData: ZMediaText[]) {
        const reducedDocuments = this.extractDocuments(jsonData, MediaTypes.AssemblingInstruction);

        if (reducedDocuments.length > 0) {
            this._assemblyInstructions$.next(reducedDocuments);
        }
    }

    private extractRepairingInstructions(jsonData: ZMediaText[]) {
        const reducedDocuments = this.extractDocuments(jsonData, MediaTypes.RepairingInstruction);

        if (reducedDocuments.length > 0) {
            this._repairingInstructions$.next(reducedDocuments);
        }
    }

    private extractDocuments(jsonData: ZMediaText[], documentType: string | string[]): ZMediaText[] {
        if (typeof documentType === 'string') {
            documentType = [documentType]; // convert param to array
        }
        const documents = jsonData.filter(document => documentType.includes(document.type));
        return this.reduceDocuments(documents, (a, b) => a.equalsIgnoringLanguage(b));
    }

    private reduceDocuments(documents: ZMediaText[], groupComperator: (a: ZMediaText, b: ZMediaText) => boolean): ZMediaText[] {
        const reducedDocuments: ZMediaText[] = [];
        documents.sort((a, b) => a.compareByLanguageTo(b, this.userLang));
        documents.forEach(x => {
            const rootBrochure = reducedDocuments.find(y => groupComperator(x, y));
            if (rootBrochure) {
                rootBrochure.alternativeLanguages.push(x);
            } else {
                x.alternativeLanguages = [];
                reducedDocuments.push(x);
                x.alternativeLanguages.push(x);
            }
        });
        return reducedDocuments;
    }

    private fetchSpecsheet(fahrzeugNr: string, locale: string): Observable<string> {
        if (!fahrzeugNr) {
            return of('');
        }
        return this.http.get<ZHtmlContent>(`${environment.specsheetUrl}(IManr='${fahrzeugNr}',ILocaleLayer='${locale}')?$format=json`)
            .pipe(
                map(data => data.content),
                tap(content => this._specsheet$.next(content))
            );
    }

    private fetchHighlights(fahrzeugNr: string, locale: string): Observable<string> {
        if (!fahrzeugNr) {
            return of('');
        }

        return this.http.get<ZHtmlContent>(`${environment.highlightsUrl}(IManr='${fahrzeugNr}',ILocaleLayer='${locale}')?$format=json`).pipe(
            map(data => data.content),
            tap(content => this._highlights$.next(content))
        );
    }
}
